/**
 * Notification Service
 * Handles API calls for notification system
 */

import axiosInstance from '../api/axiosInstance';

class NotificationService {
  constructor() {
    this.baseURL = '/notifications';
  }

  // In-app notifications
  async getInAppNotifications(params = {}) {
    const response = await axiosInstance.get(`${this.baseURL}/api/inapp/`, { params });
    return response.data;
  }

  async markAsRead(notificationId) {
    const response = await axiosInstance.patch(`${this.baseURL}/api/inapp/${notificationId}/mark_read/`);
    return response.data;
  }

  async markAllAsRead() {
    const response = await axiosInstance.patch(`${this.baseURL}/api/inapp/mark_all_read/`);
    return response.data;
  }

  async getUnreadCount() {
    const response = await axiosInstance.get(`${this.baseURL}/api/inapp/unread_count/`);
    return response.data;
  }

  // User preferences
  async getPreferences() {
    const response = await axiosInstance.get(`${this.baseURL}/api/preferences/`);
    return response.data;
  }

  async getPreferenceMatrix() {
    const response = await axiosInstance.get(`${this.baseURL}/api/preferences/matrix/`);
    return response.data;
  }

  async updatePreferences(updates) {
    const response = await axiosInstance.patch(`${this.baseURL}/api/preferences/bulk_update/`, {
      updates
    });
    return response.data;
  }

  async resetPreferencesToDefaults() {
    const response = await axiosInstance.post(`${this.baseURL}/api/preferences/reset_defaults/`);
    return response.data;
  }

  // Admin endpoints
  async getNotificationTemplates(params = {}) {
    const response = await axiosInstance.get(`${this.baseURL}/api/templates/`, { params });
    return response.data;
  }

  async createTemplate(templateData) {
    const response = await axiosInstance.post(`${this.baseURL}/api/templates/`, templateData);
    return response.data;
  }

  async updateTemplate(templateId, templateData) {
    const response = await axiosInstance.patch(`${this.baseURL}/api/templates/${templateId}/`, templateData);
    return response.data;
  }

  async previewTemplate(templateId, context) {
    const response = await axiosInstance.post(`${this.baseURL}/api/templates/${templateId}/preview/`, {
      context
    });
    return response.data;
  }

  async sendTestNotification(testData) {
    const response = await axiosInstance.post(`${this.baseURL}/api/templates/test_send/`, testData);
    return response.data;
  }

  async getNotificationHistory(params = {}) {
    const response = await axiosInstance.get(`${this.baseURL}/api/notifications/`, { params });
    return response.data;
  }

  async getNotificationSettings() {
    const response = await axiosInstance.get(`${this.baseURL}/api/settings/`, );
    return response.data;
  }

  async updateNotificationSettings(settings) {
    const response = await axiosInstance.patch(`${this.baseURL}/api/settings/`, settings);
    return response.data;
  }

  async getProviderStatus() {
    const response = await axiosInstance.get(`${this.baseURL}/api/providers/status/`);
    return response.data;
  }

  async resetProviderCircuit(channel, provider) {
    const response = await axiosInstance.post(`${this.baseURL}/api/providers/status/`, {
      channel,
      provider
    });
    return response.data;
  }

  async getMetrics(period = '24h') {
    const response = await axiosInstance.get(`${this.baseURL}/api/metrics/`, {
      params: { period }
    });
    return response.data;
  }

  async getSystemMetrics(period = '24h') {
    return this.getMetrics(period);
  }

  async testProvider(providerName) {
    const response = await axiosInstance.post(`${this.baseURL}/api/providers/${providerName}/test/`);
    return response.data;
  }

  async toggleProvider(providerName, enabled) {
    const response = await axiosInstance.patch(`${this.baseURL}/api/providers/${providerName}/`, {
      enabled
    });
    return response.data;
  }

  async testWebSocket(userId, title, body) {
    const response = await axiosInstance.post(`${this.baseURL}/api/websocket/test/`, {
      user_id: userId,
      title,
      body
    });
    return response.data;
  }
}

export const notificationService = new NotificationService();
export default notificationService;
